/**
 * Grab, move and scale dashboard panels. Controllers: squeeze (grip) on a panel to carry it,
 * squeeze with both hands to scale, squeeze twice quickly to send it home. Desktop: hold G
 * while pointing at a panel to carry it, scroll wheel to scale, H to send it home.
 */
import * as THREE from 'three';
import type { Dashboard } from './Dashboard.ts';
import type { InputManager, Pointer } from '../xr/InputManager.ts';

interface Grab {
  dashboard: Dashboard;
  /** Panel transform expressed in the pointer's frame at grab time. */
  offset: THREE.Matrix4;
}

const DOUBLE_SQUEEZE_MS = 450;
const MIN_SCALE = 0.3;
const MAX_SCALE = 2.5;

export class PanelManipulator {
  private readonly dashboards = new Set<Dashboard>();
  private readonly grabs = new Map<string, Grab>();
  private readonly wasSqueezing = new Map<string, boolean>();
  private lastSqueeze: { dashboard: Dashboard; at: number } | undefined;
  private twoHand: { dashboard: Dashboard; startDistance: number; startScale: number } | undefined;
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointerMatrix = new THREE.Matrix4();
  private readonly inverse = new THREE.Matrix4();
  private readonly tmpMatrix = new THREE.Matrix4();
  private readonly tmpPos = new THREE.Vector3();
  private readonly tmpQuat = new THREE.Quaternion();
  private readonly tmpScale = new THREE.Vector3();
  private readonly up = new THREE.Vector3(0, 1, 0);
  private hoveredByMouse: Dashboard | undefined;
  /** Tracked hands that are holding a panel (set by the pinch-and-pull gesture). */
  private readonly handHold = new Set<string>();
  private readonly onWheel = (e: WheelEvent) => this.wheel(e);
  private readonly offKey: () => void;
  /** Dev hook: the last action, for the smoke test. */
  lastAction = '';

  constructor(
    private readonly input: InputManager,
    private readonly hooks: {
      /** The last grab on a panel ended. */
      onRelease?: (dashboard: Dashboard) => void;
      /** Two-hand scale in progress. */
      onScaleGesture?: (dashboard: Dashboard, scale: number) => void;
      onHome?: (dashboard: Dashboard) => void;
    } = {},
  ) {
    window.addEventListener('wheel', this.onWheel, { passive: true });
    this.offKey = input.onKey((e) => {
      if (e.code === 'KeyH' && this.hoveredByMouse) this.sendHome(this.hoveredByMouse);
    });
  }

  add(dashboard: Dashboard): void {
    this.dashboards.add(dashboard);
  }

  remove(dashboard: Dashboard): void {
    this.dashboards.delete(dashboard);
    for (const [id, g] of this.grabs) if (g.dashboard === dashboard) this.grabs.delete(id);
    if (this.twoHand?.dashboard === dashboard) this.twoHand = undefined;
  }

  dispose(): void {
    window.removeEventListener('wheel', this.onWheel);
    this.offKey();
    this.dashboards.clear();
    this.grabs.clear();
  }

  /** The panel under the desktop mouse, when not grabbing. */
  get hoveredDashboard(): Dashboard | undefined {
    return this.hoveredByMouse;
  }

  get grabbing(): boolean {
    return this.grabs.size > 0;
  }

  /** Whether this user is holding the panel (someone else's moves wait until it is let go). */
  isHeld(dashboard: Dashboard): boolean {
    for (const g of this.grabs.values()) if (g.dashboard === dashboard) return true;
    return false;
  }

  update(): void {
    this.hoveredByMouse = undefined;
    for (const p of this.input.activePointers()) {
      const squeezing =
        p.kind === 'mouse'
          ? this.input.keysDown.has('KeyG')
          : p.kind === 'hand'
            ? this.handHold.has(p.id)
            : p.squeezing;
      const was = this.wasSqueezing.get(p.id) ?? false;
      this.wasSqueezing.set(p.id, squeezing);
      if (p.kind === 'mouse' && !squeezing) this.hoveredByMouse = this.hitTest(p);
      if (squeezing && !was) this.begin(p);
      else if (!squeezing && was) this.end(p);
      const grab = this.grabs.get(p.id);
      if (grab && squeezing) this.follow(p, grab);
    }
    if (this.twoHand) this.scaleTwoHand();
  }

  /** Pinch-and-pull: a tracked hand starts or stops holding whatever its ray is on. */
  holdWithHand(pointerId: string, on: boolean): void {
    if (on) this.handHold.add(pointerId);
    else this.handHold.delete(pointerId);
  }

  /** The panel a pointer's ray is on, if any. */
  panelUnder(p: Pointer): Dashboard | undefined {
    return this.hitTest(p);
  }

  private hitTest(p: Pointer): Dashboard | undefined {
    this.raycaster.set(p.ray.origin, p.ray.direction);
    this.raycaster.far = 8;
    const objects: THREE.Object3D[] = [];
    for (const d of this.dashboards) if (d.group.visible) objects.push(d.panel.mesh);
    const hit = this.raycaster.intersectObjects(objects, false)[0];
    if (!hit) return undefined;
    for (const d of this.dashboards) if (d.panel.mesh === hit.object) return d;
    return undefined;
  }

  private frameOf(p: Pointer, target: THREE.Matrix4): THREE.Matrix4 {
    // A pointer frame: origin at the ray origin, -z along the ray.
    this.tmpPos.copy(p.ray.origin).add(p.ray.direction);
    target.lookAt(p.ray.origin, this.tmpPos, this.up);
    target.setPosition(p.ray.origin);
    return target;
  }

  private begin(p: Pointer): void {
    const dashboard = this.hitTest(p);
    if (!dashboard) return;
    const now = performance.now();
    if (
      this.lastSqueeze &&
      this.lastSqueeze.dashboard === dashboard &&
      now - this.lastSqueeze.at < DOUBLE_SQUEEZE_MS
    ) {
      this.sendHome(dashboard);
      this.lastSqueeze = undefined;
      return;
    }
    this.lastSqueeze = { dashboard, at: now };
    this.frameOf(p, this.pointerMatrix);
    this.inverse.copy(this.pointerMatrix).invert();
    const offset = new THREE.Matrix4().multiplyMatrices(this.inverse, dashboard.group.matrixWorld);
    this.grabs.set(p.id, { dashboard, offset });
    this.lastAction = `grab:${dashboard.id}`;
    const other = [...this.grabs.entries()].find(
      ([id, g]) => id !== p.id && g.dashboard === dashboard,
    );
    if (other) {
      const distance = this.pointerDistance(p.id, other[0]);
      if (distance > 0)
        this.twoHand = { dashboard, startDistance: distance, startScale: dashboard.group.scale.x };
    }
  }

  private end(p: Pointer): void {
    const grab = this.grabs.get(p.id);
    this.grabs.delete(p.id);
    if (grab && ![...this.grabs.values()].some((g) => g.dashboard === grab.dashboard))
      this.hooks.onRelease?.(grab.dashboard);
    if (grab && this.twoHand?.dashboard === grab.dashboard) {
      this.twoHand = undefined;
      // The remaining hand keeps carrying: re-anchor its offset at the current transform.
      for (const [id, g] of this.grabs) {
        if (g.dashboard !== grab.dashboard) continue;
        const pointer = this.input.activePointers().find((x) => x.id === id);
        if (!pointer) continue;
        this.frameOf(pointer, this.pointerMatrix);
        g.offset.multiplyMatrices(
          this.inverse.copy(this.pointerMatrix).invert(),
          g.dashboard.group.matrixWorld,
        );
      }
    }
  }

  private follow(p: Pointer, grab: Grab): void {
    if (this.twoHand?.dashboard === grab.dashboard) {
      // The first grabber positions; scaling is handled separately.
      const first = [...this.grabs.keys()][0];
      if (first !== p.id) return;
    }
    this.frameOf(p, this.pointerMatrix);
    this.tmpMatrix.multiplyMatrices(this.pointerMatrix, grab.offset);
    this.tmpMatrix.decompose(this.tmpPos, this.tmpQuat, this.tmpScale);
    const group = grab.dashboard.group;
    const parent = group.parent;
    if (parent) {
      parent.worldToLocal(this.tmpPos);
      group.position.copy(this.tmpPos);
      group.quaternion.copy(
        parent.getWorldQuaternion(new THREE.Quaternion()).invert().multiply(this.tmpQuat),
      );
    } else {
      group.position.copy(this.tmpPos);
      group.quaternion.copy(this.tmpQuat);
    }
    // Panels stay upright: drop roll, keep yaw and a little pitch.
    const e = new THREE.Euler().setFromQuaternion(group.quaternion, 'YXZ');
    e.z = 0;
    e.x = THREE.MathUtils.clamp(e.x, -0.6, 0.6);
    group.quaternion.setFromEuler(e);
  }

  private pointerDistance(a: string, b: string): number {
    const pointers = this.input.activePointers();
    const pa = pointers.find((p) => p.id === a);
    const pb = pointers.find((p) => p.id === b);
    return pa && pb ? pa.ray.origin.distanceTo(pb.ray.origin) : 0;
  }

  private scaleTwoHand(): void {
    if (!this.twoHand) return;
    const ids = [...this.grabs.entries()]
      .filter(([, g]) => g.dashboard === this.twoHand!.dashboard)
      .map(([id]) => id);
    if (ids.length < 2) {
      this.twoHand = undefined;
      return;
    }
    const distance = this.pointerDistance(ids[0]!, ids[1]!);
    if (distance <= 0) return;
    const scale = THREE.MathUtils.clamp(
      (this.twoHand.startScale * distance) / this.twoHand.startDistance,
      MIN_SCALE,
      MAX_SCALE,
    );
    this.twoHand.dashboard.group.scale.setScalar(scale);
    this.hooks.onScaleGesture?.(this.twoHand.dashboard, scale);
    this.lastAction = `scale:${scale.toFixed(2)}`;
  }

  private wheel(e: WheelEvent): void {
    const target = this.hoveredByMouse ?? [...this.grabs.values()][0]?.dashboard;
    if (!target) return;
    const scale = THREE.MathUtils.clamp(
      target.group.scale.x * (e.deltaY < 0 ? 1.1 : 1 / 1.1),
      MIN_SCALE,
      MAX_SCALE,
    );
    target.group.scale.setScalar(scale);
    this.lastAction = `scale:${scale.toFixed(2)}`;
  }

  sendHome(dashboard: Dashboard): void {
    for (const [id, g] of this.grabs) if (g.dashboard === dashboard) this.grabs.delete(id);
    if (this.twoHand?.dashboard === dashboard) this.twoHand = undefined;
    dashboard.goHome();
    this.hooks.onHome?.(dashboard);
    this.lastAction = `home:${dashboard.id}`;
  }

  sendAllHome(): void {
    for (const d of this.dashboards) this.sendHome(d);
  }
}
